interface VoiceSession {
  id: symbol;
  isValid: () => boolean;
  finish: () => Promise<void>;
  start: () => Promise<boolean>;
}

let activeVoiceSession: VoiceSession | null = null;
let transitionQueue: Promise<void> = Promise.resolve();
let recoveryQueue: Promise<void> = Promise.resolve();
/** 页面内原生通道最近接纳的完整快照；只驻留内存，供下一输入框继承。 */
let previousVoiceSessionSnapshot: string | null = null;

export function getPreviousVoiceSessionSnapshot() {
  return previousVoiceSessionSnapshot;
}

/** 只有当前会话持有者可以更新快照，旧会话的迟到响应不得覆盖新会话缓存。 */
export function rememberVoiceSessionSnapshot(
  sessionId: symbol,
  snapshot: string
) {
  if (activeVoiceSession?.id !== sessionId) return false;
  previousVoiceSessionSnapshot = snapshot;
  return true;
}

/**
 * 串行执行原生语音恢复操作。新的录音启动/预热前会等待该队列，
 * 避免旧会话（如录音中卸载组件、切后台兜底）的全局 cancel 迟到后误取消新录音。
 */
export function queueVoiceSessionRecovery(
  recovery: () => Promise<unknown>
) {
  const task = recoveryQueue.then(async () => {
    try {
      await recovery();
    } catch (_error) {
      // 恢复操作只用于尽力清理原生状态，失败不能阻塞后续语音会话。
    }
  });
  recoveryQueue = task;
  return task;
}

export async function waitForVoiceSessionRecovery() {
  while (true) {
    const pendingRecovery = recoveryQueue;
    await pendingRecovery;
    if (pendingRecovery === recoveryQueue) return;
  }
}

/**
 * 串行切换全局语音会话：先等待原生恢复和上一会话完整收尾，再启动新会话。
 * recordAudio/getAsrResult 是全局原生通道，不能由多个 TextareaBox 实例并发使用。
 */
export function activateVoiceSession(session: VoiceSession) {
  const transition = transitionQueue.then(async () => {
    await waitForVoiceSessionRecovery();
    // 排队期间已经松手、取消或卸载的手势不能干扰当前有效会话。
    if (!session.isValid()) return;
    if (activeVoiceSession?.id === session.id) return;

    const previousSession = activeVoiceSession;
    if (previousSession) {
      await previousSession.finish();
      // finish 可能触发新的 cancel 恢复，启动下一会话前必须再次等待。
      await waitForVoiceSessionRecovery();
      if (activeVoiceSession?.id === previousSession.id) {
        activeVoiceSession = null;
      }
      // 等待上一会话收尾期间手势可能已经结束，此时不可再启动录音。
      if (!session.isValid()) return;
    }

    activeVoiceSession = session;
    try {
      const didStart = await session.start();
      if (!didStart) {
        releaseVoiceSession(session.id);
      }
    } catch (error) {
      releaseVoiceSession(session.id);
      throw error;
    }
  });

  // 单次切换失败不能阻断之后的语音会话。
  transitionQueue = transition.catch(() => undefined);
  return transition;
}

/** 仅允许当前持有者释放会话，避免旧异步任务误释放新会话。 */
export function releaseVoiceSession(sessionId: symbol) {
  if (activeVoiceSession?.id === sessionId) {
    activeVoiceSession = null;
  }
}
